import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportAPI } from '../../../services/api';
import { ReportPreflightCompanyResult, ReportRequest, ReportStatus } from '../../../types/api';

const REPORT_STATUS_STORAGE_KEY = 'report_statuses_v3';
const REPORT_STATUS_CACHE_TTL_MS = 30 * 60 * 1000;

type ReportStatusCachePayload = {
  profileKey?: string;
  statuses?: ReportStatus[];
  downloadedIds?: string[];
  cachedAt?: number;
} | ReportStatus[];

const toCacheableStatuses = (statuses: ReportStatus[]) =>
  statuses.filter((status) => status.status === 'processing' || status.status === 'pending' || status.status === 'completed');

const readReportStatusCache = (currentProfileKey: string) => {
  const cached = sessionStorage.getItem(REPORT_STATUS_STORAGE_KEY);
  if (!cached) {
    return null;
  }

  try {
    const parsed = JSON.parse(cached) as ReportStatusCachePayload;
    if (Array.isArray(parsed)) {
      const statuses = toCacheableStatuses(parsed);
      return statuses.length > 0 ? { statuses, downloadedIds: new Set<string>() } : null;
    }

    if (parsed?.profileKey && parsed.profileKey !== currentProfileKey) {
      return null;
    }

    if (typeof parsed?.cachedAt === 'number' && Date.now() - parsed.cachedAt > REPORT_STATUS_CACHE_TTL_MS) {
      sessionStorage.removeItem(REPORT_STATUS_STORAGE_KEY);
      return null;
    }

    const statuses = Array.isArray(parsed?.statuses) ? toCacheableStatuses(parsed.statuses) : [];
    const downloadedIds = Array.isArray(parsed?.downloadedIds)
      ? new Set(parsed.downloadedIds.filter((value): value is string => typeof value === 'string'))
      : new Set<string>();

    if (statuses.length === 0 && downloadedIds.size === 0) {
      return null;
    }

    return { statuses, downloadedIds };
  } catch {
    return null;
  }
};

const writeReportStatusCache = (currentProfileKey: string, statuses: ReportStatus[], downloadedIds: Set<string>) => {
  const cacheable = toCacheableStatuses(statuses);
  if (cacheable.length > 0 || downloadedIds.size > 0) {
    sessionStorage.setItem(REPORT_STATUS_STORAGE_KEY, JSON.stringify({
      profileKey: currentProfileKey,
      statuses: cacheable,
      downloadedIds: Array.from(downloadedIds),
      cachedAt: Date.now(),
    }));
    return;
  }

  sessionStorage.removeItem(REPORT_STATUS_STORAGE_KEY);
};

type GenerateReportOptions = {
  request: ReportRequest;
  requiresPreflight?: boolean;
  validationError?: string | null;
  emptySelectionMessage?: string;
};

type DownloadReportOptions = {
  onBeforeDownload?: () => void;
};

type UseRunReportJobOptions = {
  currentProfileKey: string;
  reportScopeKey: string;
};

export const useRunReportJob = ({ currentProfileKey, reportScopeKey }: UseRunReportJobOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preflightIssues, setPreflightIssues] = useState<ReportPreflightCompanyResult[]>([]);
  const [reportStatuses, setReportStatuses] = useState<ReportStatus[]>([]);
  const [downloadedIds, setDownloadedIds] = useState<Set<string>>(new Set());
  const pollingIdsRef = useRef<Set<string>>(new Set());
  const dismissedIdsRef = useRef<Set<string>>(new Set());
  const pollAbortControllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    setError(null);
    setPreflightIssues([]);
    setReportStatuses([]);
    setDownloadedIds(new Set());
    dismissedIdsRef.current = new Set();

    const cached = readReportStatusCache(currentProfileKey);
    if (!cached) {
      return;
    }

    if (cached.statuses.length > 0) {
      setReportStatuses(cached.statuses);
    }
    if (cached.downloadedIds.size > 0) {
      setDownloadedIds(cached.downloadedIds);
    }
  }, [currentProfileKey, reportScopeKey]);

  useEffect(() => {
    writeReportStatusCache(currentProfileKey, reportStatuses, downloadedIds);
  }, [currentProfileKey, downloadedIds, reportStatuses]);

  useEffect(() => {
    const controllers = pollAbortControllersRef.current;

    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  const upsertReportStatus = useCallback((status: ReportStatus) => {
    if (dismissedIdsRef.current.has(status.reportId)) {
      return;
    }

    setReportStatuses((prev) => {
      const index = prev.findIndex((item) => item.reportId === status.reportId);
      if (index === -1) {
        return [status, ...prev];
      }

      const next = [...prev];
      next[index] = { ...next[index], ...status };
      return next;
    });
  }, []);

  const pollReportStatus = useCallback(async (reportId: string) => {
    if (pollingIdsRef.current.has(reportId)) {
      return;
    }

    pollingIdsRef.current.add(reportId);

    const controller = new AbortController();
    pollAbortControllersRef.current.set(reportId, controller);

    try {
      const maxAttempts = 90;
      const pollIntervalMs = 2000;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (dismissedIdsRef.current.has(reportId) || controller.signal.aborted) {
          break;
        }

        let status: ReportStatus;
        try {
          status = await reportAPI.getReportStatus(reportId, controller.signal);
        } catch (err: any) {
          if (controller.signal.aborted || dismissedIdsRef.current.has(reportId)) {
            break;
          }
          if (err?.response?.status === 404) {
            break;
          }
          throw err;
        }

        upsertReportStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }
    } finally {
      pollingIdsRef.current.delete(reportId);
      pollAbortControllersRef.current.delete(reportId);
    }
  }, [upsertReportStatus]);

  useEffect(() => {
    const processing = reportStatuses.filter((report) => report.status === 'processing' || report.status === 'pending');
    processing.forEach((report) => {
      void pollReportStatus(report.reportId);
    });
  }, [pollReportStatus, reportStatuses]);

  const generateReport = useCallback(async ({ request, requiresPreflight = false, validationError, emptySelectionMessage = 'Välj minst en.' }: GenerateReportOptions) => {
    if (!request.resultUnits || request.resultUnits.length === 0) {
      setError(emptySelectionMessage);
      return;
    }

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setPreflightIssues([]);

    try {
      if (requiresPreflight) {
        await reportAPI.preflightReport(request);
      }

      const { reportId } = await reportAPI.generateReport(request);
      upsertReportStatus({
        reportId,
        status: 'processing',
        createdAt: new Date().toISOString(),
      });

      await pollReportStatus(reportId);
    } catch (err: any) {
      if (err?.suppressUserError) {
        return;
      }

      const invalidCompanies = err?.response?.data?.invalidCompanies;
      if (Array.isArray(invalidCompanies) && invalidCompanies.length > 0) {
        setPreflightIssues(invalidCompanies);
      }

      setError(
        err.response?.data?.error ||
        err.response?.data?.reason ||
        err.message ||
        'Rapportfel.'
      );
    } finally {
      setLoading(false);
    }
  }, [pollReportStatus, upsertReportStatus]);

  const downloadReport = useCallback(async (report: ReportStatus, options?: DownloadReportOptions) => {
    options?.onBeforeDownload?.();

    setReportStatuses((prev) => prev.filter((item) => item.reportId !== report.reportId));

    const controller = new AbortController();

    try {
      const { blob, fileName } = await reportAPI.downloadReportFile(report.reportId, controller.signal);
      const resolvedName = fileName || report.fileName || `report_${report.reportId}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = resolvedName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadedIds((prev) => new Set(prev).add(report.reportId));
    } catch (err: any) {
      if (controller.signal.aborted) {
        return;
      }

      upsertReportStatus(report);
      setError('Kunde inte ladda hem rapporten.');
    }
  }, [upsertReportStatus]);

  const completedReports = useMemo(
    () => reportStatuses.filter((report) => report.status === 'completed' && !downloadedIds.has(report.reportId)),
    [downloadedIds, reportStatuses]
  );
  const latestCompletedReport = completedReports[0] ?? null;
  const primaryButtonIsDownloadMode = latestCompletedReport !== null;

  return {
    loading,
    error,
    preflightIssues,
    latestCompletedReport,
    primaryButtonIsDownloadMode,
    generateReport,
    downloadReport,
  };
};